import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { QueryKeys } from 'utils/constants';
import { LocationDetail } from './useGetLocations';
import { useAxios } from 'providers/AxiosProvider';
import { useToasts } from 'providers/ToastsProvider';

export interface WeatherCondition {
  code: number;
  icon: string;
  text: string;
}

export interface AlertDetail {
  headline: string;
  msgtype: string;
  severity: string;
  urgency: string;
  areas: string;
  category: string;
  certainty: string;
  event: string;
  note: string;
  effective: string;
  expires: string;
  desc: string;
  instruction: string;
}

export interface AlertSummary {
  alert: AlertDetail[];
}

export interface CurrentConditionsDetail {
  cloud: number;
  condition: WeatherCondition;
  feelslike_c: number;
  feelslike_f: number;
  gust_kph: number;
  gust_mph: number;
  humidity: number;
  is_day: number;
  last_updated: string;
  last_updated_epoch: number;
  precip_in: number;
  precip_mm: number;
  pressure_in: number;
  pressure_mb: number;
  temp_c: number;
  temp_f: number;
  uv: number;
  vis_km: number;
  vis_miles: number;
  wind_degree: number;
  wind_dir: string;
  wind_kph: number;
  wind_mph: number;
}

export interface AstrologicalDetail {
  is_moon_up: number;
  is_sun_up: number;
  moon_illumination: number;
  moon_phase: string;
  moonrise: string;
  moonset: string;
  sunrise: string;
  sunset: string;
}

export interface ForecastDayDetail {
  avghumidity: number;
  avgtemp_c: number;
  avgtemp_f: number;
  avgvis_km: number;
  avgvis_miles: number;
  condition: WeatherCondition;
  daily_chance_of_rain: number;
  daily_chance_of_snow: number;
  daily_will_it_rain: number;
  daily_will_it_snow: number;
  maxtemp_c: number;
  maxtemp_f: number;
  maxwind_kph: number;
  maxwind_mph: number;
  mintemp_c: number;
  mintemp_f: number;
  totalprecip_in: number;
  totalprecip_mm: number;
  totalsnow_cm: number;
  uv: number;
}

export interface ForecastHourDetail {
  chance_of_rain: number;
  chance_of_snow: number;
  cloud: number;
  condition: WeatherCondition;
  dewpoint_c: number;
  dewpoint_f: number;
  feelslike_c: number;
  feelslike_f: number;
  gust_kph: number;
  gust_mph: number;
  heatindex_c: number;
  heatindex_f: number;
  humidity: number;
  is_day: number;
  precip_in: number;
  precip_mm: number;
  pressure_in: number;
  pressure_mb: number;
  temp_c: number;
  temp_f: number;
  time: string;
  time_epoch: number;
  uv: number;
  vis_km: number;
  vis_miles: number;
  will_it_rain: number;
  will_it_snow: number;
  wind_degree: number;
  wind_dir: string;
  wind_kph: number;
  wind_mph: number;
  windchill_c: number;
  windchill_f: number;
}

export interface ForecastDaySummary {
  astro: AstrologicalDetail;
  date: string;
  date_epoch: number;
  day: ForecastDayDetail;
  hour: ForecastHourDetail[];
}

export interface ForecastDetail {
  forecastday: ForecastDaySummary[];
}

export interface Weather {
  pk: string;
  coordsId: string;
  provider: string;
  providerData: {
    location: LocationDetail;
    current: CurrentConditionsDetail;
    forecast: ForecastDetail;
    alerts: AlertSummary;
  };
  updatedAt: string;
}

export const useGetForecast = (lat: number, lon: number) => {
  const { createToast } = useToasts();
  const axios = useAxios();
  const isEnabled = !!lat && !!lon;

  const getForecast = async (lat: number, lon: number): Promise<Weather> => {
    const response = await axios.request({
      url: `/api/forecasts?lat=${lat}&lon=${lon}&alerts=yes`,
    });

    createToast('Forecast updated.');

    return response.data;
  };

  return useQuery<Weather, AxiosError>({
    queryKey: [QueryKeys.Forecasts, lat, lon],
    queryFn: () => getForecast(lat, lon),
    enabled: isEnabled,
    staleTime: 1000 * 60 * 15, // 15m
  });
};
